import type { UploadScreenshotBody } from '@/types/api';

/**
 * Prepares an admin-chosen image file for a manual screenshot upload.
 *
 * The dashboard does the resizing and encoding so the API stays image-library-free: we draw the
 * file to a canvas at a capped width, encode a full and a thumbnail WebP, and base64 them (no
 * `data:` prefix — the API decodes raw base64). WebP keeps the payload small; if the browser can't
 * encode WebP (older Safari), we fall back to PNG for both parts. The API re-validates the bytes.
 */

const FULL_MAX_WIDTH = 1600;
const THUMBNAIL_WIDTH = 480;

export async function prepareScreenshotUpload(file: File): Promise<UploadScreenshotBody> {
  if (!file.type.startsWith('image/')) {
    throw new Error('Please choose an image file (PNG, JPEG or WebP).');
  }

  const bitmap = await createImageBitmap(file);
  try {
    const fullCanvas = drawScaled(bitmap, FULL_MAX_WIDTH);
    const thumbCanvas = drawScaled(bitmap, THUMBNAIL_WIDTH);

    let contentType: UploadScreenshotBody['contentType'] = 'image/webp';
    let fullBlob = await encode(fullCanvas, 'image/webp', 0.85);
    if (fullBlob.type !== 'image/webp') {
      // The browser ignored the requested type and fell back — encode PNG for both parts instead.
      contentType = 'image/png';
      fullBlob = await encode(fullCanvas, 'image/png');
    }
    const thumbBlob = await encode(
      thumbCanvas,
      contentType,
      contentType === 'image/webp' ? 0.8 : undefined,
    );

    return {
      contentType,
      image: await blobToBase64(fullBlob),
      thumbnail: await blobToBase64(thumbBlob),
    };
  } finally {
    bitmap.close();
  }
}

function drawScaled(bitmap: ImageBitmap, maxWidth: number): HTMLCanvasElement {
  const scale = Math.min(1, maxWidth / bitmap.width);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));

  const context = canvas.getContext('2d');
  if (!context) throw new Error('Could not process the image in this browser.');
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  return canvas;
}

function encode(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Could not encode the image.'))),
      type,
      quality,
    );
  });
}

async function blobToBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = '';
  const chunk = 0x8000; // Chunked so a large image does not overflow the argument stack.
  for (let offset = 0; offset < bytes.length; offset += chunk) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunk));
  }
  return btoa(binary);
}
